import { createContext, useContext } from "react";
import type { To } from "react-router";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import {
  useListQuery,
  type ListQueryState,
} from "@shared-core/domain/useListQuery";
import type { CustomerSession } from "@shared-core/data/customer-api-types";
import {
  PortalReviewContext,
  type PortalReviewControls,
} from "./PortalReviewContext";

// Who is signed in to the portal and what the session says they hold at the customer they are working
// as (GET /customer-api/session, v2). The person is whoever the token says: there is no way to look at
// the portal as someone else here. The mock build alone adds a review tool (`review`: a persona
// switcher, see mock/MockPortalReviewRoot.tsx) because its fixtures belong to a handful of made-up
// people; in the real build `review` is null.
//
// The customer a person with several customers is working as is chosen by the landing gate before this
// provider mounts (PortalLayout's `usePortalSignInGate`) and travels on every request as
// `X-Portal-Customer` (shared-core's portal-customer-header); this provider only re-reads the session
// when that choice changes.

interface PortalViewerContextValue {
  /**
   * Changes whenever the person or the customer they are working as changes, so a page that puts it in
   * its fetch dependencies reloads under the new viewer.
   */
  viewKey: string;
  /** The mock build's persona switcher; null in the real build. */
  review: PortalReviewControls | null;
  /** The caller's own effective access at the customer they are working as, per GET /session. */
  sessionState: ListQueryState<CustomerSession>;
  retrySession: () => void;
  /**
   * Turns an absolute portal path (a `paths.portalX(...)` result, or a
   * server-supplied route-shaped string like a spend breakdown row's
   * `drill_url`) into the `To` a reused page's `<Link>` should actually
   * navigate to.
   *
   * The customer build's own router mounts these paths at the app root, so
   * there this is the identity function. Staff-view mounts the SAME pages
   * under `/portal-view/person/:personId/...` or `/portal-view/preview/...`
   * (see staff-view/routes.tsx) — an absolute `to` there only gets the
   * router's real basename re-added, dropping the person/preview segment
   * (and, for a preview target, the query string the whole spec lives in).
   * `StaffViewIdentityProvider` overrides this with a relative-path,
   * search-preserving implementation; every reused page must call this
   * instead of handing `paths.portalX(...)` straight to `<Link to=...>`.
   */
  buildPortalUrl: (path: string) => To;
  /**
   * True only in staff-view act-for mode (customer-portal-view-as amendment,
   * rule 5: "Enter act mode deliberately"). Always `false` for the real
   * customer build (a customer person is never "acting for" themselves) and
   * for staff-view "view as"/"preview" (read-only). Gates the confirm step
   * a page must show before any outward-submitting write
   * (PortalArtworkCheckPage's "Check this file").
   */
  acting: boolean;
  /** The customer name to show in an act-for confirm step. `null` outside
   *  act-for mode. */
  actingCustomerName: string | null;
  /**
   * True when the staff caller may switch on "Act for…" for the target they are looking at (the session's
   * `view.can_act_for`, with a target that can be acted for at all: never a preview). Always `false` for the real
   * customer build, and `null` in the staff view until the session has answered. A read-only page uses it to say
   * whether switching to act would let the caller do what the page only shows, and waits for the answer.
   */
  canActFor: boolean | null;
  /**
   * True whenever this page is rendered inside the staff-view shell
   * (view-as or act-for; `StaffViewIdentityProvider`), `false` for the real
   * customer build (`PortalViewerProvider`). Distinct from `acting`: a
   * staff caller who is merely viewing (not acting) still has
   * `isStaffView === true` with `acting === false`, whereas the real
   * customer's own session is `isStaffView === false` with `acting`
   * always `false` too -- a reused page needs to tell those two
   * `acting === false` cases apart (e.g. the Proofs decision panel: a
   * real customer decides normally, a staff viewer sees "Switch to
   * acting to decide").
   */
  isStaffView: boolean;
}

function identityBuildPortalUrl(path: string): To {
  return path;
}

const PortalViewerContext = createContext<PortalViewerContextValue | null>(
  null,
);

export function PortalViewerProvider({
  customerId,
  children,
}: {
  /** The customer the person chose (the landing gate's decision); null when there is nothing to choose between. */
  customerId: string | null;
  children: React.ReactNode;
}) {
  const adapter = useCustomerDataAdapter();
  const review = useContext(PortalReviewContext);
  const viewKey = `${review?.persona.id ?? ""}:${customerId ?? ""}`;

  const { state: sessionState, retry: retrySession } = useListQuery(
    () => adapter.getSession(),
    [adapter, viewKey],
  );

  const value: PortalViewerContextValue = {
    viewKey,
    review,
    sessionState,
    retrySession,
    buildPortalUrl: identityBuildPortalUrl,
    acting: false,
    actingCustomerName: null,
    canActFor: false,
    isStaffView: false,
  };

  return (
    <PortalViewerContext.Provider value={value}>
      {children}
    </PortalViewerContext.Provider>
  );
}

export function usePortalViewer(): PortalViewerContextValue {
  const ctx = useContext(PortalViewerContext);
  if (!ctx) {
    throw new Error("usePortalViewer must be used inside PortalViewerProvider");
  }
  return ctx;
}

/**
 * True while staff only look at the customer's portal: the staff view with "Act for…" off. Every page shows what the
 * customer sees (Ben, 26 Oct 2026), so a page that changes something draws its controls disabled or not at all when
 * this is true, and says why with `StaffViewReadOnlyNote`. The one reading of "view only": a page never works it out
 * from `isStaffView` and `acting` itself.
 */
export function useIsReadOnlyView(): boolean {
  const { isStaffView, acting } = usePortalViewer();
  return isStaffView && !acting;
}

// Exported so staff-view/StaffViewIdentityProvider.tsx can supply a value
// through this exact context instance without going through
// PortalViewerProvider's own session fetch: staff-view reads its own session
// (the staff mirror route) and supplies it. The reused portal pages
// (PortalOrdersPage etc.) import usePortalViewer from this module directly,
// so providing a value requires this exact Context object -- there is no
// other seam.
export { PortalViewerContext };
export type { PortalViewerContextValue };
